/**
 * Hook for fetching and managing document authorship information (git blame).
 * Provides debounced fetching on hover to avoid excessive API calls.
 */

import { useState, useCallback, useRef } from 'react'
import { documentsAPI } from '@/api/documents'
import { log, logError, logDebug } from '@utils/logger'

interface AuthorshipInfo {
  user_id: string | null
  username: string | null
  full_name: string | null
  created_at: string | null
  sentence_text?: string
  sentence_index?: number
}

interface UseAuthorshipOptions {
  documentId: string | null | undefined
  enabled?: boolean
}

interface UseAuthorshipReturn {
  authorship: AuthorshipInfo | null
  isLoading: boolean
  error: Error | null
  fetchAuthorship: (position: number) => void
  clearAuthorship: () => void
}

export function useAuthorship(options: UseAuthorshipOptions): UseAuthorshipReturn {
  const { documentId, enabled = true } = options
  
  const [authorship, setAuthorship] = useState<AuthorshipInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastPositionRef = useRef<number | null>(null)
  
  const fetchAuthorship = useCallback(async (position: number) => {
    logDebug('[useAuthorship] fetchAuthorship called:', { documentId, enabled, position })
    
    if (!documentId || !enabled) {
      logDebug('[useAuthorship] Skipping - no documentId or not enabled')
      return
    }
    
    // Debounce: only fetch if position changed and after a short delay
    if (lastPositionRef.current === position) {
      logDebug('[useAuthorship] Skipping - same position as last fetch')
      return
    }
    
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }
    
    // Debounce fetch by 300ms
    fetchTimeoutRef.current = setTimeout(async () => {
      lastPositionRef.current = position
      setIsLoading(true)
      setError(null)
      
      logDebug('[useAuthorship] Making API call:', { documentId, position })
      
      try {
        const data = await documentsAPI.getAuthorship(documentId, position)
        logDebug('[useAuthorship] API response:', data)
        
        // Transform API response to AuthorshipInfo
        const authorshipInfo: AuthorshipInfo = {
          user_id: data.user_id || null,
          username: data.username || null,
          full_name: data.full_name || null,
          created_at: data.created_at || null,
          sentence_text: data.sentence_text,
          sentence_index: data.sentence_index,
        }
        
        setAuthorship(authorshipInfo)
      } catch (err) {
        logError('[useAuthorship] Error fetching authorship:', err)
        setError(err instanceof Error ? err : new Error('Failed to fetch authorship'))
        setAuthorship(null)
      } finally {
        setIsLoading(false)
      }
    }, 300)
  }, [documentId, enabled])
  
  const clearAuthorship = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
      fetchTimeoutRef.current = null
    }
    lastPositionRef.current = null
    setAuthorship(null)
    setError(null)
    setIsLoading(false)
  }, [])
  
  return {
    authorship,
    isLoading,
    error,
    fetchAuthorship,
    clearAuthorship,
  }
}

